// components/CopyProjectDataDialog.jsx

import React, { useState, useEffect } from 'react';
import { useCopyProjectData } from './useCopyProjectData';
import { COPY_OPTIONS, getAllTableNames } from './copyDataConstants';
import ProjectSelector from './ProjectSelector.jsx';
import ConfigurationSelector from './ConfigurationSelector.jsx';
import CopyResults from './CopyResults.jsx';




const CopyProjectDataDialog = ({
  isOpen,
  onClose,
  newProjectId,
  corporateAccount,
  requestingForCorporateAccount,
  loginToken
}) => {
  const [step, setStep] = useState(1); // 1: Select Project, 2: Select Config, 3: Results
  const [selectedProject, setSelectedProject] = useState('');
  const [selectedConfigurations, setSelectedConfigurations] = useState([]);
  const [copyAll, setCopyAll] = useState(true);

  const {
    isLoading,
    projects,
    copyResults,
    error,
    fetchProjects,
    copyProjectData,
    resetState,
  } = useCopyProjectData(loginToken);

  // Filter out the current project from the list of projects to copy from
  const availableProjects = projects.filter(project => project.project_id !== newProjectId);

  // Dialog styles matching CreateProjectDialog
  const dialogStyles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1200, // Higher than CreateProjectDialog
      padding: '20px'
    },
    dialog: {
      backgroundColor: 'white',
      borderRadius: '8px',
      width: '600px',
      maxWidth: '90vw',
      height: 'auto',
      maxHeight: '90vh',
      boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden'
    },
    header: {
      backgroundColor: '#CCCCFF',
      textAlign: 'center',
      padding: '15px 20px',
      borderRadius: '8px 8px 0 0',
      fontSize: '18px',
      fontWeight: 'bold',
      flexShrink: 0
    },
    content: {
      flex: 1,
      overflowY: 'auto',
      padding: '20px',
      maxHeight: 'calc(90vh - 140px)'
    },
    footer: {
      padding: '15px 20px',
      borderTop: '1px solid #e5e7eb',
      backgroundColor: '#f9fafb',
      borderRadius: '0 0 8px 8px',
      flexShrink: 0
    }
  };

  useEffect(() => {
    if (isOpen && requestingForCorporateAccount) {
        console.log('Fetching projects for corporate account:', requestingForCorporateAccount);
                console.log(' corporate account:', corporateAccount);

      fetchProjects(requestingForCorporateAccount);
    }
  }, [isOpen, requestingForCorporateAccount]);

  useEffect(() => {
    if (isOpen) {
      // Initialize with mandatory configurations + all others if copyAll is true
      const mandatoryConfigs = COPY_OPTIONS.filter(option => option.mandatory).map(option => option.id);
      const allConfigs = COPY_OPTIONS.map(option => option.id);

      if (copyAll) {
        setSelectedConfigurations(allConfigs);
      } else {
        setSelectedConfigurations(mandatoryConfigs);
      }
    }
  }, [isOpen, copyAll]);

  const handleProjectSelect = (projectId) => {
    setSelectedProject(projectId);
    setStep(2);
  };

  const handleConfigurationChange = (configIds) => {
    // Ensure mandatory configurations are always included
    const mandatoryConfigs = COPY_OPTIONS.filter(option => option.mandatory).map(option => option.id);
    const finalSelection = [...new Set([...mandatoryConfigs, ...configIds])];

    setSelectedConfigurations(finalSelection);
    setCopyAll(finalSelection.length === COPY_OPTIONS.length);
  };

  const handleCopyAllToggle = (checked) => {
    setCopyAll(checked);
    const mandatoryConfigs = COPY_OPTIONS.filter(option => option.mandatory).map(option => option.id);

    if (checked) {
      setSelectedConfigurations(COPY_OPTIONS.map(option => option.id));
    } else {
      setSelectedConfigurations(mandatoryConfigs);
    }
  };

  const getTableNamesFromSelection = () => {
    const selectedOptions = COPY_OPTIONS.filter(option =>
      selectedConfigurations.includes(option.id)
    );

    const tableNames = [];
    selectedOptions.forEach(option => {
      if (Array.isArray(option.tableName)) {
        tableNames.push(...option.tableName);
      } else {
        tableNames.push(option.tableName);
      }
    });

    return tableNames;
  };

  const handleStartCopy = async () => {
    const tableNames = getTableNamesFromSelection();

    const copyRequest = {
      corporate_account: corporateAccount,
      copy_from_project_id: selectedProject,
      copy_to_project_id: newProjectId,
      requesting_for_corporate_account: requestingForCorporateAccount,
      table_names: tableNames,
    };

    try {
      await copyProjectData(copyRequest);
      setStep(3);
    } catch (err) {
      // Error handling is managed by the hook
    }
  };

  const handleClose = () => {
    resetState();
    setStep(1);
    setSelectedProject('');
    setSelectedConfigurations([]);
    setCopyAll(true);
    onClose();
  };

  const renderStepContent = () => {
    switch (step) {
      case 1:
        return (
          <ProjectSelector
            projects={availableProjects} // Use filtered projects here
            selectedProject={selectedProject}
            onProjectSelect={handleProjectSelect}
            isLoading={isLoading}
            error={error}
          />
        );
      case 2:
        return (
          <ConfigurationSelector
            copyOptions={COPY_OPTIONS}
            selectedConfigurations={selectedConfigurations}
            copyAll={copyAll}
            onConfigurationChange={handleConfigurationChange}
            onCopyAllToggle={handleCopyAllToggle}
            onStartCopy={handleStartCopy}
            onBack={() => setStep(1)}
            isLoading={isLoading}
          />
        );
      case 3:
        return (
          <CopyResults
            results={copyResults}
            onClose={handleClose}
            onCopyMore={() => setStep(1)}
          />
        );
      default:
        return null;
    }
  };

  const getFooterButtons = () => {
    switch (step) {
      case 1:
        return null;
      case 2:
        const canProceed = selectedConfigurations.length > 0;
        const spinnerStyle = {
          width: '16px',
          height: '16px',
          border: '2px solid #ffffff',
          borderTopColor: 'transparent',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          marginRight: '8px'
        };

        return (
          <>
            <button
              onClick={() => setStep(1)}
              style={{
                padding: '8px 16px',
                color: '#374151',
                backgroundColor: '#f3f4f6',
                borderRadius: '4px',
                border: '1px solid #d1d5db',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              Back
            </button>
            <button
              onClick={handleStartCopy}
              disabled={!canProceed || isLoading}
              style={{
                padding: '8px 24px',
                borderRadius: '4px',
                fontSize: '14px',
                fontWeight: 'bold',
                border: 'none',
                cursor: canProceed && !isLoading ? 'pointer' : 'not-allowed',
                backgroundColor: canProceed && !isLoading ? '#C8F4DE' : '#f3f4f6',
                color: canProceed && !isLoading ? 'black' : '#6b7280',
                display: 'flex',
                alignItems: 'center'
              }}
            >
              {isLoading && <div style={spinnerStyle}></div>}
              {isLoading ? 'Copying...' : `Copy ${selectedConfigurations.length} Configuration${selectedConfigurations.length !== 1 ? 's' : ''}`}
            </button>
          </>
        );
      case 3:
        const isSuccess = copyResults?.status === 'Success';
        const isPartialSuccess = copyResults?.status === 'Partial Success';
        const isFailed = copyResults?.status === 'Failed';

        return (
          <>
            <button
              onClick={handleClose}
              style={{
                padding: '8px 16px',
                backgroundColor: '#C8F4DE',
                color: 'black',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 'bold'
              }}
            >
              Close
            </button>
            {(isFailed || isPartialSuccess) && (
              <button
                onClick={() => setStep(1)}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#e0f2fe',
                  color: '#0277bd',
                  border: '1px solid #81d4fa',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Copy More Data
              </button>
            )}
          </>
        );
      default:
        return null;
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div style={dialogStyles.overlay}>
        <div style={dialogStyles.dialog}>
          {/* Fixed Header */}
          <div style={dialogStyles.header}>
            Copy Project Configuration
          </div>

          {/* Scrollable Content */}
          <div style={dialogStyles.content}>
            {/* Step indicator */}
            <div style={{ marginBottom: '24px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                {[1, 2, 3].map((stepNum) => (
                  <div key={stepNum} style={{ display: 'flex', alignItems: 'center' }}>
                    <div
                      style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '14px',
                        fontWeight: 'bold',
                        backgroundColor: step >= stepNum ? '#3b82f6' : '#e5e7eb',
                        color: step >= stepNum ? 'white' : '#6b7280'
                      }}
                    >
                      {stepNum}
                    </div>
                    {stepNum < 3 && (
                      <div
                        style={{
                          width: '48px',
                          height: '4px',
                          backgroundColor: step > stepNum ? '#3b82f6' : '#e5e7eb'
                        }}
                      />
                    )}
                  </div>
                ))}
              </div>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginTop: '8px',
                fontSize: '12px',
                color: '#6b7280'
              }}>
                <span>Select Project</span>
                <span>Choose Configuration</span>
                <span>View Results</span>
              </div>
            </div>

            {renderStepContent()}
          </div>

          {/* Fixed Footer */}
          <div style={dialogStyles.footer}>
            <div style={{ display: 'flex', justifyContent: 'center', gap: '16px' }}>
              {getFooterButtons()}
            </div>
          </div>
        </div>
      </div>

      {/* Add CSS for spin animation */}
      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </>
  );
};

export default CopyProjectDataDialog;